import React from 'react';
import { Button } from 'antd';
import {
  useDeleteLightPropsMutation,
  FindManyUserSceneAssetsDocument,
  useDeleteUserSceneAssetsMutation,
} from '@src/components/generated/graphql';
import { CloseOutlined } from '@ant-design/icons';
import NftAssets from '@src/components/side-bar-menu/nft-assets/NftAssets';
import SceneObjects from '@src/components/side-bar-menu/scene-objects/SceneObjects';
import SceneAnimations from '@src/components/side-bar-menu/scene-animations/SceneAnimations';
import SplitContainer from '@src/components/side-bar-menu/split-container/SplitContainer';
import CreatedAssets from '../asset-store/created-assets/CreatedAssets';
import SideDrawerElement from './sideDrawerElement';
import { SIDE_MENU_EVENTS } from './sideMenu';
import { AudioSideDrawer } from '../side-bar-menu/audio-assets/audioSideDrawer';
import DefaultButton from '../ui-kit/buttons/DefaultButton';
import { ShopifyMetadata } from '../scene/react-three-components/sceneAsset';
import TextChat from '../side-bar-menu/text-chat/TextChat';
import ScanScene from '../side-bar-menu/scan-scene/ScanScene';
import AIAvatars, {
  AIAvatarConfig,
} from '../side-bar-menu/AI-avatars/AIAvatars';
import Assets3D from './ui/Assets3D';
import { ASSET_TYPE } from '../scene/react-three-components/sceneAsset';
import Quests from '../side-bar-menu/quests/Quests';
import Portals from '../side-bar-menu/portals/Portals';
import PackZepeto from '../side-bar-menu/packs-zepeto/PackZepeto';
import { zepetoItems } from './zepeto-items';
import { useSession } from 'next-auth/react';
import ShopifyProducts from '../side-bar-menu/shopify/ShopifyProducts';
import HideAndSeek from '../side-bar-menu/games/hide-and-seek/HideAndSeek';
import GraphicsSettings from '../side-bar-menu/graphics-settings/GraphicsSettings';
import { AddYoutube } from '../side-bar-menu/stream/AddYoutube';
import { AddTwitch } from '../side-bar-menu/stream/AddTwitch';
import { AddIframe } from '../side-bar-menu/stream/AddIframe';
import TourConfig from '../side-bar-menu/tour/TourConfig';
import SceneConfig from '../side-bar-menu/scene-config/SceneConfig';
import Lights from './ui/Lights';
import Sky from './ui/Sky';
import ThreeDGeneration from '../side-bar-menu/3d-generation/ThreeDGeneration';
import styles from './side-drawer.module.css';
import MMLObjects from '../side-bar-menu/mml-objects/MMLObjects';
import TimelineNodeManager from '../side-bar-menu/timeline-node-manager/TimelineNodeManager';
import { isUndefined } from 'util/helpers';
import type { LIGHT_TYPE } from '../scene/react-three-components/lightTemplate';
import { EnvMapType } from '@src/components/generated/graphql';

const SIDE_MENU_EXCEPTIONS = [
  SIDE_MENU_EVENTS.SKY,
  SIDE_MENU_EVENTS.AI_AVATARS,
  SIDE_MENU_EVENTS.TIMELINE_NODE_MANAGER,
];

export type TemplateLight = {
  type: LIGHT_TYPE;
  intensity: number;
  color: number; // HEX for example 0x111111
  groundColor?: number; // HEX for example 0x111111
  position?: number[];
  isActiveShadow?: boolean;
  decay?: number;
  distance?: number;
};

export default function SideDrawer({
  isOpen,
  onClose,
  type,
  inProgress,
  items,
  selectableAssets,
  callBack,
  studioState,
  className,
  setSelectedObject,
  setSelectableAssets,
  currentCameraConfig,
  callBackSave,
  setLightTemplate,
  zepetoKey = '',
  deleteAsset,
  loadSceneTemplate,
  saveScene,
  camera,
  sceneId,
  scene,
}) {
  const {
    lightTemplate,
    selectedSkybox,
    selectedModel,
    selectedSceneTemplate,
    setIsEnableShadowLight,
  } = studioState;
  const { data: session } = useSession();
  const [deleteLightProps] = useDeleteLightPropsMutation();
  const [deleteAssetScene] = useDeleteUserSceneAssetsMutation();

  function onChangeCSMLight(checked) {
    setIsEnableShadowLight(checked);
  }

  function loadSkyBox({
    skyBoxNumber,
    isSkyBox2025,
    skyBoxUrl,
    hdriType,
    fileName,
    envMapType,
  }: {
    skyBoxNumber: number;
    isSkyBox2025: boolean;
    skyBoxUrl: string;
    hdriType: string;
    fileName: string[];
    envMapType: EnvMapType;
  }) {
    callBack({
      type: SIDE_MENU_EVENTS.SKY,
      index: skyBoxNumber,
      hdriType: hdriType,
      hdriIntensity: selectedSkybox.hdriIntensity,
      object: {
        index: skyBoxNumber,
        url: skyBoxUrl,
        fileName,
      },
      isSkyBox2025: isSkyBox2025 || false,
      envMapType: envMapType || EnvMapType.Hdri,
    });
  }

  function loadLightsTemplate(lights: TemplateLight[], lightTemplate?: number) {
    if (!isUndefined(lightTemplate)) {
      setLightTemplate(lightTemplate);
    }

    lights.forEach((light) => {
      callBack({
        type: SIDE_MENU_EVENTS.LIGHTSADDED,
        typeLight: light.type,
        intensity: light.intensity,
        color: light.color,
        position: light.position,
        castShadow: light.isActiveShadow || false,
        groundColor: light.groundColor,
        decay: light.decay,
        distance: light.distance,
      });
    });
  }

  return (
    <div
      className={`container-sidedrawer ${
        isOpen && !SIDE_MENU_EXCEPTIONS.includes(type) ? 'active' : ''
      } ${className}`}
    >
      <Button
        icon={<CloseOutlined style={{ fontSize: 16, color: '#333333' }} />}
        type='link'
        data-cy='close-side-menu'
        className='close-side-menu-button'
        onClick={() => {
          onClose();
        }}
      />
      <div className={styles.contentWrapper}>
        {[SIDE_MENU_EVENTS.LIBRARY_3D, SIDE_MENU_EVENTS.MFE_WORLDS].includes(
          type
        ) ? (
          <Assets3D
            items={items}
            callBack={callBack}
            inProgress={inProgress}
            type={type}
            session={session}
            selectedSceneTemplate={selectedSceneTemplate}
          />
        ) : (
          <>
            {![
              SIDE_MENU_EVENTS.NFT_ASSETS,
              SIDE_MENU_EVENTS.OBJECTS_IN_SCENE,
              SIDE_MENU_EVENTS.SCREEN_SHARE,
              SIDE_MENU_EVENTS.YOUTUBE,
              SIDE_MENU_EVENTS.TWITCH,
              SIDE_MENU_EVENTS.IFRAME,
              SIDE_MENU_EVENTS.AUDIOS,
              SIDE_MENU_EVENTS.QUESTS,
              SIDE_MENU_EVENTS.HIDE_AND_SEEK,
              SIDE_MENU_EVENTS.GRAPHICS_SETTINGS,
              SIDE_MENU_EVENTS.LIGHTS,
              SIDE_MENU_EVENTS.SKY,
              SIDE_MENU_EVENTS.THREE_D_GENERATION,
            ].includes(type) && (
              <>
                {items &&
                  items.map((element, index) => {
                    const hasObjectIndex = element?.object?.index !== undefined;
                    const isSelected =
                      element?.index === lightTemplate ||
                      (hasObjectIndex &&
                        selectedSkybox.index === element?.object?.index) ||
                      element?.baseModelURL === selectedSceneTemplate;
                    return (
                      <SideDrawerElement
                        classes={
                          isSelected ? 'sidedrawer-element-selected' : ''
                        }
                        inProgress={inProgress}
                        key={element.id || `${type}-${index}`}
                        thumbnail={element.thumbnail}
                        callback={() => {
                          callBack({
                            ...element,
                            type,
                            index,
                            sceneThumbnail: element.thumbnail,
                          });
                        }}
                      />
                    );
                  })}
              </>
            )}
            {type === SIDE_MENU_EVENTS.SKY && (
              <Sky
                isOpen={isOpen}
                setIsOpen={onClose}
                items={items}
                callBack={callBack}
                type={type}
                selectedSkybox={selectedSkybox}
                styles={styles}
              />
            )}
            {type === SIDE_MENU_EVENTS.LIGHTS && (
              <Lights
                items={items}
                callBack={callBack}
                type={type}
                lightTemplate={lightTemplate}
                onChangeCSMLight={onChangeCSMLight}
                styles={styles}
                selectedSkybox={selectedSkybox}
                selectedSceneTemplate={selectedSceneTemplate}
              />
            )}
            {type === SIDE_MENU_EVENTS.SCREEN_SHARE && (
              <DefaultButton
                type='secondary'
                style={{ minHeight: 32 }}
                onClick={() => {
                  callBack({ type, name: items[0].name });
                }}
              >
                <span style={{ color: '#333333' }}> add Screenshare</span>
              </DefaultButton>
            )}

            {type === SIDE_MENU_EVENTS.YOUTUBE && (
              <AddYoutube
                selectedModel={selectedModel}
                callBack={callBack}
                type={type}
                items={items}
              />
            )}

            {type === SIDE_MENU_EVENTS.IFRAME && (
              <AddIframe callBack={callBack} items={items} />
            )}

            {type === SIDE_MENU_EVENTS.TWITCH && (
              <AddTwitch
                selectedModel={selectedModel}
                callBack={callBack}
                items={items}
              />
            )}

            {type === SIDE_MENU_EVENTS.ZEPETO_PACK && (
              <PackZepeto
                items={zepetoItems[zepetoKey]}
                inProgress={inProgress}
                callback={callBack}
              />
            )}

            {type === SIDE_MENU_EVENTS.AUDIOS && (
              <AudioSideDrawer
                key={'audios-side-bar-menu'}
                audios={items}
                callback={(url, name) => {
                  callBack({ url, type, name });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.NFT_ASSETS && (
              <NftAssets
                key={'nft-asset-side-bar-menu'}
                typeNFTs='marketplace'
                callback={(
                  name: string,
                  url: string,
                  type: SIDE_MENU_EVENTS
                ) => {
                  callBack({ name, type, url, thumbnail: url });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.LAND_ASSETS && (
              <NftAssets
                key={'nft-asset-side-bar-menu'}
                typeNFTs='land'
                callback={(
                  name: string,
                  url: string,
                  type: SIDE_MENU_EVENTS,
                  thumbnail: string
                ) => {
                  callBack({ name, type, baseModelURL: url, thumbnail });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.OBJECTS_IN_SCENE && (
              <SplitContainer
                topComponent={
                  <SceneObjects
                    key={'scene-objects'}
                    assets={selectableAssets}
                    camera={camera}
                    selectObject={setSelectedObject}
                    deleteAsset={deleteAsset}
                    scene={scene}
                  />
                }
                bottomComponent={
                  <SceneAnimations
                    key={'scene-animations'}
                    assets={selectableAssets}
                  />
                }
              />
            )}
            {type === SIDE_MENU_EVENTS.CREATED_ASSETS && (
              <CreatedAssets
                key={'created-assets'}
                callback={(
                  name: string,
                  url: string,
                  type: SIDE_MENU_EVENTS,
                  marketId: string
                ) => {
                  callBack({ name, type, url, thumbnail: url, marketId });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.SHOPIFY && (
              <ShopifyProducts
                key={'shopify'}
                callback={(
                  name: string,
                  url: string,
                  type: SIDE_MENU_EVENTS,
                  shopifyMetadata: ShopifyMetadata
                ) => {
                  callBack({ name, url, type, shopifyMetadata });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.QUESTS && <Quests />}
            {type === SIDE_MENU_EVENTS.HIDE_AND_SEEK && <HideAndSeek />}
            {type === SIDE_MENU_EVENTS.AI_AVATARS && (
              <AIAvatars
                key={'AI-avatars'}
                selectedModel={selectedModel}
                setIsOpen={onClose}
                isOpen={isOpen}
                loadAsset={(
                  name: string,
                  url: string,
                  config: AIAvatarConfig
                ) => {
                  callBack({
                    name,
                    type: SIDE_MENU_EVENTS.AI_AVATARS,
                    url,
                    config,
                    thumbnail: url,
                  });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.TEXT_CHAT && (
              <TextChat key={'text-chat'} />
            )}
            {type === SIDE_MENU_EVENTS.SCAN_SCENE && (
              <ScanScene key={'scan-scene'} />
            )}
            {type === SIDE_MENU_EVENTS.GRAPHICS_SETTINGS && (
              <GraphicsSettings
                key={'graphic-settings'}
                cameraConfigProps={{
                  currentCameraConfig,
                  callBack,
                  type,
                  callBackSave,
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.TOUR && <TourConfig />}
            {type === SIDE_MENU_EVENTS.SCENE_CONFIG && (
              <SceneConfig
                loadSceneTemplate={loadSceneTemplate}
                saveScene={saveScene}
                loadSkyBox={loadSkyBox}
                loadLightsTemplate={loadLightsTemplate}
              />
            )}
            {type === SIDE_MENU_EVENTS.PORTALS && (
              <Portals
                key={'portals'}
                loadAsset={(name: string, url: string) => {
                  callBack({
                    name,
                    type: SIDE_MENU_EVENTS.PORTALS,
                    url,
                    thumbnail: url,
                  });
                }}
              />
            )}
            {type === SIDE_MENU_EVENTS.THREE_D_GENERATION && (
              <ThreeDGeneration
                key={'three-d-generation'}
                addToScene={callBack}
              />
            )}
            {type === SIDE_MENU_EVENTS.MML_OBJECTS && (
              <MMLObjects
                key={'mml-objects'}
                callback={(data) => {
                  callBack({
                    ...data,
                    type: SIDE_MENU_EVENTS.MML_OBJECTS,
                  });
                }}
                selectedModel={selectedModel}
              />
            )}
          </>
        )}
        <TimelineNodeManager
          key='event-timeline-node-manager'
          isOpen={isOpen && type === SIDE_MENU_EVENTS.TIMELINE_NODE_MANAGER}
          setIsOpen={onClose}
          selectableAssets={selectableAssets}
          userSceneId={sceneId}
          saveTimelineRef={{ current: null }}
        />
      </div>
    </div>
  );
}
